/* eslint-disable @typescript-eslint/no-explicit-any */
import { RefObject, KeyboardEventHandler } from 'react';
import { IconNames } from '@hallmark/web.core.buttons.icon-button';
import { CtaPropTypes, CtaStyle } from '@hallmark/web.core.buttons.text-link';
import { TypographyVariants, HtmlTagOverrides } from '@hallmark/web.core.typography.typography';
import { BrandColors as TabsBrandColors } from '@hallmark/web.styles.colors';
import { CtaStyleDetail, BottomPadding } from '@hallmark/web.util.shared-types';

export type AccessibilityKeys = {
  end: 35;
  home: 36;
  left: 37;
  up: 38;
  right: 39;
  down: 40;
  delete: 46;
  enter: 13;
  space: 32;
};

export enum EventKeys {
  ArrowLeft = 'ArrowLeft',
  ArrowUp = 'ArrowUp',
  ArrowRight = 'ArrowRight',
  ArrowDown = 'ArrowDown',
  End = 'End',
  Home = 'Home',
}

export type TabObjectType = {
  label?: string;
  content?: JSX.Element;
  buttonRef?: RefObject<HTMLButtonElement>;
  icon?: IconNames;
};

export type TabContentProps = {
  tabLabel?: string;
  tabContent?: any;
  tabIndex?: number;
};

export type ContentfulImageProps = {
  altText: string;
  ariaLabel?: string | null;
  image: {
    file: {
      url: string;
    };
  };
  name: string;
  referenceId?: string | null;
  sku?: string | null;
};

export type TabItemProps = {
  /** Element ref passed to tab navigation button */
  buttonRef: RefObject<HTMLButtonElement>;
  /** Tab text label */
  label: string;
  /** Is tab currently selected? */
  selected: boolean;
  /** HTML element ID */
  id: string;
  /** SVG to display behind tabs as background image */
  sharedBackgroundSVG?: ContentfulImageProps;
  /** Fires when tab is clicked */
  onClick: () => void;
  /** Used for keyboard navigation between tabs */
  onKeyDown: KeyboardEventHandler<HTMLDivElement>;
  /** Used for keyboard navigation between tabs */
  onKeyUp: KeyboardEventHandler<HTMLDivElement>;
  /** Tab index in tabArray */
  index: number;
  /** Color of SVG to display behind tabs as background image */
  svgCustomColor: TabsBrandColors;
  /** Conditional styling for large content */
  marginLarge?: boolean;
  /** icon name */
  icon: IconNames | JSX.Element | any;
};

export type TabItem = {
  tabLabel: string;
  tabContent: JSX.Element;
  tabIcon: IconNames;
  buttonRef: RefObject<HTMLButtonElement>;
};

export type TabsTypesProps = {
  /** Class to append to Tabs component */
  addClass?: string;
  /** Set ID attribute of HTML element */
  anchorId?: string;
  /** Set this to just show the tab content without tabs */
  breakTabs?: boolean;
  /** Color of SVG to display behind tabs as background image */
  tabImageColor?: TabsBrandColors;
  /** Tabs items */
  tabsItems: TabItem[];
  /** Tab content background color */
  backgroundColor?: string;
  /** Padding at bottom of tabs content */
  bottomPadding: BottomPadding;
  /** TextBlock element to display above tabs */
  textBlock?: TextBlockProps;
  /** Color of headline text */
  headlineColor?: TabsBrandColors;
  /** Headline styling -- h2, h3, h4, h5, h6 */
  headlineStyle2?: TypographyVariants;
  /** Defines the type of Component being used in the Tabs */
  componentType?: string;
};

export type EyebrowProps = {
  /** Text to be rendered in the component */
  text: string;
  /** Color of the text */
  color?: TabsBrandColors;
  /** Overrides the default tag that the component is rendered as */
  eyebrowTag?: HtmlTagOverrides;
  /** ID for testing purposes */
  testId?: string;
};

export enum PresetLayout {
  LargeCentered = 'Large Centered',
  LargeLeft = 'Large Left',
  SmallCentered = 'Small Centered',
  SmallLeft = 'Small Left',
}

export enum TypographyLayout {
  TypographyLeft = 'typographyLeft',
  TypographyCenter = 'typographyCenter',
}

export enum TextWidth {
  TextBlockLarge = 'textblock-large',
  TextBlockSmall = 'textblock-small',
}

export enum ContentAlign {
  AlignCenter = 'align-center',
  AlignLeft = 'align-left',
}

export type PresetLayoutDetail = {
  typographyLayout: TypographyLayout;
  textWidth: TextWidth;
  contentAlign: ContentAlign;
  eyebrow: string;
  headline: string;
  headlineColor: TabsBrandColors;
  headlineStyle?: TypographyVariants;
  bodyCopy?: any;
  primaryCtaStyle?: CtaStyle;
  bottomPadding?: BottomPadding;
};

export type TextBlockCtaStyleDetail = Pick<CtaStyleDetail, 'backgroundColor' | 'btnStyle' | 'textColor'>;

export type TextBlockProps = {
  /** Contentful driven property */
  componentType?: string;
  anchorId?: string;
  backgroundColor?: string;
  presetLayout: PresetLayout;
  eyebrow?: string;
  headline?: string;
  headlineColor?: TabsBrandColors;
  headlineStyle?: TypographyVariants;
  bodyCopy?: any;
  primaryCta?: CtaPropTypes;
  primaryCtaStyle?: CtaStyle;
  bottomPadding: BottomPadding;
  testId?: string;
  // Require eyebrow OR headline
} & ({ eyebrow: string } | { headline: string });

export { TypographyVariants as TabsHeadlineStyle };
export { HtmlTagOverrides };
export { TabsBrandColors };
